<template>
    <div style="width: 100%;">
        <div v-if="!isEditing" class="ck-view-wrapper">
            <div class="ck-edit-bar">
                <button class="ck-edit-btn" title="עריכה" @click="isEditing = true">
                    <b-icon pack="fa" icon="pen" size="is-small"></b-icon>
                    <span>עריכה</span>
                </button>
            </div>
            <div class="ck-view-content" v-html="ckEditorContent || placeholder"></div>
        </div>
        <div v-else class="ck-editor-wrapper" :style="wrapperStyle">
            <div class="ck-edit-bar">
                <button class="ck-edit-btn ck-edit-btn--active" title="סיום עריכה" @click="isEditing = false">
                    <b-icon pack="fa" icon="pen" size="is-small"></b-icon>
                    <span>סיום עריכה</span>
                </button>
            </div>
            <ckeditor :editor="ckEditor" v-model="ckEditorContent" :config="ckEditorConfig" @ready="onEditorReady"></ckeditor>
            <div class="ck-word-count-bar">
                <span>מילים: {{ wordCountStats.words }}</span>
                <span>תווים: {{ wordCountStats.characters }}</span>
            </div>
        </div>
    </div>
</template>

<script>
// Add custom Tab plugin, for option to use TAB key to indent
class TabPlugin {
    constructor(editor) {
        this.editor = editor;
    }

    init() {
        // Just prevent TAB from navigating the browser
        this.editor.keystrokes.set('tab', (_data, stop) => {
            // Use the built-in indent command
            this.editor.execute('indent');
            stop();
        });

        this.editor.keystrokes.set('shift+tab', (_data, stop) => {
            // Use the built-in outdent command
            this.editor.execute('outdent');
            stop();
        });
    }
}

const {
    ClassicEditor,
    Alignment,
    // Autoformat,
    Base64UploadAdapter,
    AutoImage,
    AutoLink,
    Autosave,
    BalloonToolbar,
    BlockQuote,
    Bold,
    Bookmark,
    CloudServices,
    Code,
    Essentials,
    FontBackgroundColor,
    FontColor,
    FontFamily,
    FontSize,
    GeneralHtmlSupport,
    Heading,
    Highlight,
    HorizontalLine,
    HtmlComment,
    HtmlEmbed,
    ImageBlock,
    ImageCaption,
    ImageInline,
    ImageInsertViaUrl,
    ImageResize,
    ImageStyle,
    ImageTextAlternative,
    ImageToolbar,
    ImageUpload,
    Indent,
    IndentBlock,
    Italic,
    Link,
    LinkImage,
    List,
    ListProperties,
    //
    MediaEmbed,
    Paragraph,
    PasteFromMarkdownExperimental,
    PasteFromOffice,
    RemoveFormat,
    SpecialCharacters,
    SpecialCharactersArrows,
    SpecialCharactersCurrency,
    SpecialCharactersEssentials,
    SpecialCharactersLatin,
    SpecialCharactersMathematical,
    SpecialCharactersText,
    Strikethrough,
    Style,
    Subscript,
    Superscript,
    Table,
    TableCaption,
    TableCellProperties,
    TableColumnResize,
    TableProperties,
    TableToolbar,
    TextTransformation,
    TodoList,
    SourceEditing,
    Underline,
    Mention,
    FindAndReplace,
    PageBreak,
    ShowBlocks,
    WordCount
} = CKEDITOR;

const {
    ExportPdf,
    ExportWord,
    LineHeight,
    CaseChange,
    FormatPainter,
    ImportWord,
    MultiLevelList,
    PasteFromOfficeEnhanced,
    TableOfContents,
    SlashCommand,
} = CKEDITOR_PREMIUM_FEATURES;

export default {
    name: 'NewTask',
    props: {
        content: String,
        animation: {
            type: String,
            default: 'fade',
        },
        placeholder: String,
        backgroundColor: {
            type: String,
            default: null,
        },
        borderColor: {
            type: String,
            default: null,
        },
        alwaysEdit: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        wrapperStyle() {
            const style = {};
            if (this.backgroundColor) style.backgroundColor = this.backgroundColor;
            if (this.borderColor) {
                style.border = `2px solid ${this.borderColor}`;
                style.borderRadius = '3px';
            }
            return style;
        },
    },
    data() {
        return {
            isEditing: this.alwaysEdit,
            ckEditorContent: this.content || '',
            wordCountStats: { words: 0, characters: 0 },
            ckEditorConfig: {
                language: 'he',
                mediaEmbed: {
                    previewsInData: true
                },
                toolbar: {
                    items: [
                        'heading',
                        '|',
                        'fontSize',
                        'fontFamily',
                        'lineHeight',
                        'fontColor',
                        'fontBackgroundColor',
                        '|',
                        'bold',
                        'italic',
                        'underline',
                        'caseChange',
                        'formatPainter',
                        '|',
                        'link',
                        'insertTable',
                        'highlight',
                        'pageBreak',
                        '|',
                        'alignment',
                        '|',
                        'bulletedList',
                        'numberedList',
                        'todoList',
                        'multilevelList',
                        '|',
                        'outdent',
                        'indent',
                        '|',
                        'tableOfContents',
                        'findAndReplace',
                        'showBlocks',
                        'importWord',
                        '|',
                        'exportPdf',
                        'exportWord'
                    ],
                    shouldNotGroupWhenFull: false
                },
                plugins: [
                    Alignment,
                    // Autoformat,
                    Base64UploadAdapter,
                    AutoImage,
                    AutoLink,
                    Autosave,
                    BalloonToolbar,
                    BlockQuote,
                    Bold,
                    Bookmark,
                    CloudServices,
                    Code,
                    Essentials,
                    FontBackgroundColor,
                    FontColor,
                    FontFamily,
                    FontSize,
                    GeneralHtmlSupport,
                    Heading,
                    Highlight,
                    HorizontalLine,
                    HtmlComment,
                    HtmlEmbed,
                    ImageBlock,
                    ImageCaption,
                    ImageInline,
                    ImageInsertViaUrl,
                    ImageResize,
                    ImageStyle,
                    ImageTextAlternative,
                    ImageToolbar,
                    ImageUpload,
                    Indent,
                    IndentBlock,
                    Italic,
                    Link,
                    LinkImage,
                    List,
                    ListProperties,
                    //
                    MediaEmbed,
                    Paragraph,
                    PasteFromMarkdownExperimental,
                    PasteFromOffice,
                    RemoveFormat,
                    SpecialCharacters,
                    SpecialCharactersArrows,
                    SpecialCharactersCurrency,
                    SpecialCharactersEssentials,
                    SpecialCharactersLatin,
                    SpecialCharactersMathematical,
                    SpecialCharactersText,
                    Strikethrough,
                    Style,
                    Subscript,
                    Superscript,
                    Table,
                    TableCaption,
                    TableCellProperties,
                    TableColumnResize,
                    TableProperties,
                    TableToolbar,
                    TextTransformation,
                    TodoList,
                    SourceEditing,
                    Underline,
                    ExportPdf,
                    ExportWord,
                    LineHeight,
                    CaseChange,
                    FormatPainter,
                    ImportWord,
                    MultiLevelList,
                    PasteFromOfficeEnhanced,
                    TableOfContents,
                    SlashCommand,
                    Mention,
                    FindAndReplace,
                    PageBreak,
                    ShowBlocks,
                    WordCount,
                ].filter(Boolean),
                balloonToolbar: ['bold', 'italic', '|', 'link', '|', 'bulletedList', 'numberedList'],
                fontFamily: {},
                fontSize: {
                    options: [10, 12, 14, 'default', 18, 20, 22],
                },
                lineHeight: {
                    options: ['default', 1, 1.1, 1.25, 1.5, 2, 2.5, 3]
                },
                heading: {
                    options: [
                        {
                            model: 'paragraph',
                            title: 'Paragraph',
                            class: 'ck-heading_paragraph'
                        },
                        {
                            model: 'heading1',
                            view: 'h1',
                            title: 'Heading 1',
                            class: 'ck-heading_heading1'
                        },
                        {
                            model: 'heading2',
                            view: 'h2',
                            title: 'Heading 2',
                            class: 'ck-heading_heading2'
                        },
                        {
                            model: 'heading3',
                            view: 'h3',
                            title: 'Heading 3',
                            class: 'ck-heading_heading3'
                        },
                        {
                            model: 'heading4',
                            view: 'h4',
                            title: 'Heading 4',
                            class: 'ck-heading_heading4'
                        },
                        {
                            model: 'heading5',
                            view: 'h5',
                            title: 'Heading 5',
                            class: 'ck-heading_heading5'
                        },
                        {
                            model: 'heading6',
                            view: 'h6',
                            title: 'Heading 6',
                            class: 'ck-heading_heading6'
                        }
                    ]
                },
                htmlSupport: {
                    allow: [
                        {
                            name: /^.*$/,
                            styles: true,
                            attributes: true,
                            classes: true
                        }
                    ],
                },
                image: {
                    toolbar: [
                        'toggleImageCaption',
                        'imageTextAlternative',
                        '|',
                        'imageStyle:inline',
                        'imageStyle:wrapText',
                        'imageStyle:breakText',
                        '|',
                        'resizeImage'
                    ]
                },
                licenseKey: process.env.VUE_APP_CKEDITOR_LICENSE_KEY,
                exportPdf: {
                    stylesheets: [
                        'https://cdn.ckeditor.com/ckeditor5/44.1.0/ckeditor5.css',
                        'https://cdn.ckeditor.com/ckeditor5-premium-features/44.1.0/ckeditor5-premium-features.css'
                    ],
                    fileName: 'document.pdf',
                    converterOptions: {
                        format: 'A4',
                        margin_top: '20mm',
                        margin_bottom: '20mm',
                        margin_right: '12mm',
                        margin_left: '12mm',
                        page_orientation: 'portrait'
                    }
                },
                exportWord: {
                    stylesheets: [
                        'https://cdn.ckeditor.com/ckeditor5/44.1.0/ckeditor5.css',
                        'https://cdn.ckeditor.com/ckeditor5-premium-features/44.1.0/ckeditor5-premium-features.css'
                    ],
                    fileName: 'document.docx',
                    converterOptions: {
                        format: 'A4',
                        margin_top: '20mm',
                        margin_bottom: '20mm',
                        margin_right: '12mm',
                        margin_left: '12mm',
                        orientation: 'portrait'
                    }
                },
                link: {
                    addTargetToExternalLinks: true,
                    defaultProtocol: 'https://',
                    decorators: {
                        toggleDownloadable: {
                            mode: 'manual',
                            label: 'Downloadable',
                            attributes: {
                                download: 'file'
                            }
                        }
                    }
                },
                list: {
                    properties: {
                        styles: true,
                        startIndex: true,
                        reversed: true
                    }
                },
                indentBlock: {
                    offset: 20, // 20px per indent step
                    unit: 'px',
                },
                menuBar: {
                    isVisible: true
                },
                placeholder: '',
                style: {
                    definitions: [
                        {
                            name: 'Article category',
                            element: 'h3',
                            classes: ['category']
                        },
                        {
                            name: 'Title',
                            element: 'h2',
                            classes: ['document-title']
                        },
                        {
                            name: 'Subtitle',
                            element: 'h3',
                            classes: ['document-subtitle']
                        },
                        {
                            name: 'Info box',
                            element: 'p',
                            classes: ['info-box']
                        },
                        {
                            name: 'Side quote',
                            element: 'blockquote',
                            classes: ['side-quote']
                        },
                        {
                            name: 'Marker',
                            element: 'span',
                            classes: ['marker']
                        },
                        {
                            name: 'Spoiler',
                            element: 'span',
                            classes: ['spoiler']
                        },
                        {
                            name: 'Code (dark)',
                            element: 'pre',
                            classes: ['fancy-code', 'fancy-code-dark']
                        },
                        {
                            name: 'Code (bright)',
                            element: 'pre',
                            classes: ['fancy-code', 'fancy-code-bright']
                        }
                    ]
                },
                table: {
                    contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties']
                },
                extraPlugins: [TabPlugin],
                wordCount: {
                    onUpdate: stats => {
                        this.wordCountStats = stats;
                    }
                },
            },
            ckEditor: ClassicEditor,
        };
    },
    methods: {
        close() {
            this.$emit('onClose');
        },
        save() {
            this.$emit('onSave', { html: this.ckEditorContent });
        },
        getHTML() {
            return this.ckEditorContent;
        },
        isEmpty() {
            return !this.ckEditorContent || this.ckEditorContent.trim() === '';
        },
        setData(value) {
            this.ckEditorContent = value;
        },
        clearEditor() {
            this.ckEditorContent = '';
        },
        onEditorReady(editor) {
            const clipboardPlugin = editor.plugins.get('ClipboardPipeline');
            clipboardPlugin.on('inputTransformation', (evt, data) => {
                const html = data.dataTransfer?.getData('text/html') || '';
                const isFromWord = html.includes('mso-') || html.includes('MsoNormal') || html.includes('xmlns:w=');
                if (!isFromWord) return;

                editor.editing.view.change(writer => {
                    this._stripWordStyles(writer, data.content);
                });
            }, { priority: 'high' });
        },
        _stripWordStyles(writer, node) {
            if (!node.is('element') && !node.is('documentFragment')) return;
            if (node.is('element')) {
                const stylesToStrip = [
                    'font-family', 'font-size', 'line-height', 'unicode-bidi',
                    'margin', 'margin-top', 'margin-bottom', 'margin-left', 'margin-right',
                ];
                for (const style of stylesToStrip) {
                    if (node.hasStyle(style)) {
                        writer.removeStyle(style, node);
                    }
                }
            }
            for (const child of [...node.getChildren()]) {
                this._stripWordStyles(writer, child);
            }
        },
    },
    watch: {
        ckEditorContent(newValue) {
            this.$emit('onEditorChange', newValue);
        }
    },
};
</script>

<style scoped lang="scss">
.ck-view-wrapper {
    width: 100%;
    border: 1px dashed #ddd;
    border-radius: 3px;
}

.ck-edit-bar {
    display: flex;
    justify-content: flex-start;
    padding: 4px 6px;
    background: #f9f9f9;
    border-bottom: 1px solid #eee;
    border-radius: 3px 3px 0 0;
}

.ck-view-content {
    padding: 8px;
    min-height: 32px;
    ::v-deep ol {
        list-style-type: decimal;
        margin: revert;
        padding: revert;
    }

    ::v-deep ul {
        list-style-type: disc;
        margin: revert;
        padding: revert;
    }
}

.ck-edit-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    background: white;
    border: 1px solid #ddd;
    border-radius: 4px;
    cursor: pointer;
    padding: 3px 8px;
    font-size: 12px;
    color: #555;

    &--active {
        background: #e8f4ff;
        border-color: #2fc9e8;
        color: #0099bb;
    }
}

.ck-editor-wrapper {
    width: 100%;
    overflow: visible;
}

::v-deep .ck.ck-editor {
    overflow: visible;
}

::v-deep .ck.ck-editor__top {
    overflow: visible;
}

::v-deep .ck.ck-toolbar {
    overflow: visible;
}

.ck-word-count-bar {
    display: flex;
    gap: 16px;
    justify-content: flex-start;
    padding: 4px 8px;
    font-size: 12px;
    color: #888;
    border: 1px solid #ddd;
    border-top: none;
    background: #fafafa;
}

::v-deep .ck-editor__editable {
    ol {
        list-style-type: decimal;
    }

    ol ol {
        list-style-type: lower-alpha;
    }

    ol ol ol {
        list-style-type: lower-roman;
    }

    h1 {
        font-size: 2em;
        font-weight: bold;
        margin: 0.67em 0;
    }

    h2 {
        font-size: 1.5em;
        font-weight: bold;
        margin: 0.75em 0;
    }

    h3 {
        font-size: 1.17em;
        font-weight: bold;
        margin: 0.83em 0;
    }

    h4 {
        font-size: 1em;
        font-weight: bold;
        margin: 1.12em 0;
    }

    h5 {
        font-size: 0.83em;
        font-weight: bold;
        margin: 1.5em 0;
    }

    h6 {
        font-size: 0.75em;
        font-weight: bold;
        margin: 1.67em 0;
    }
}
</style>
